/**
 * 経路を道路中心線へスナップする後処理 (軽量マップマッチング)。
 *
 * Mapbox の徒歩グラフは車道と**分離歩道・横断歩道**を別エッジとして持つ (streets の
 * OSM 由来)。そのため Directions の生経路は「歩道を並走 → 横断歩道で直角に渡る」
 * 微細ジオメトリになり、案内図の矢印としては不要に粗く見える (実測: 阪本小学校
 * A1 経路の step 命令が walkway/crosswalk のみで構成され、交差点に 4 頂点のホップ)。
 * `walkway_bias=-1` は歩道⇄車道の接続エッジで直角ジョグが増え逆効果 (実測 312°→453°)。
 *
 * 案内図の矢印はスキーマティックに**道路中心**を通るのが正なので、頂点を中心線へ
 * 射影して集約する。割当は**頂点独立の最近傍ではなく、経路全体での一貫性**で決める
 * (Viterbi/DP)。頂点独立だと「曖昧なら棄権」のゲートが必要になり、棄権した頂点が
 * 歩道座標のまま残って「道路でない部分に被る折れ」になる (実測: ユーザー第3報) —
 * 一貫性コストで解けば棄権自体が不要になる。
 *
 * コスト設計:
 * - 射影距離 (m)。スナップしない選択は UNSNAPPED_COST_M の定額 (敷地内通路・
 *   公園の小道など中心線が無い区間は自然にこちらが選ばれ、原形を保つ)。
 * - 線の乗り換えに LINE_CHANGE_COST_M — 並走2中心線の間で頂点ごとに反転する
 *   フラッピングを抑止 (タイル境界の重複 feature は幾何が同じなので実害なし)。
 * - 形状歪み |スナップ後の頂点間距離 − 元の頂点間距離| — 逆走・横飛びを抑止。
 *
 * その他の防御:
 * - **角度ゲート**: 進行方向と並走 (≤50°) するセグメントだけを候補にする。
 * - **回避リング**: 射影点が避難先/物件ポリゴンの内側に落ちる候補は除外
 *   (trim が確立した「経路はポリゴン外」の不変条件をスナップが後段で破らない)。
 * - **スパイクガード**: スナップで頂点の折れ角が元より 45° 超悪化したら原座標へ戻す。
 * - **ゼロ方向ガード**: 三重複頂点などで進行方向が定まらない頂点は候補なし扱い。
 * - **角補完**: 別線に乗り換わる箇所は中心線同士の交点を挿入し、角を実在の交差点上に
 *   描く (ほぼ平行な別 feature = タイル分割ペアはゴミ交点になるため角度差 ≥30° のみ)。
 *
 * このファイルは意図的に Vite/Mapbox 非依存 (bake スクリプトからも使える純関数)。
 */

export interface SnapRouteOptions {
  /** 頂点から中心線までの最大スナップ距離 (m)。幅広道路の歩道→中心線も覆う。 */
  maxSnapM?: number;
  /** 進行方向と中心線セグメントの最大角度差 (度)。並走のみ候補にする。 */
  maxAngleDeg?: number;
  /** 射影点がこのリング群の内側に落ちる候補は除外する (原座標を保つ)。 */
  avoidRings?: Array<Array<[number, number]>>;
}

/**
 * 候補ゲート (m)。射影距離 + 角度ペナルティがこれを超える線は候補にしない。
 * 30 = UNSNAPPED_COST_M(28) より広く取り、採否の実質判断はコスト比較に委ねる。
 * 25 だと川沿い遊歩道 (道路まで 22.6m) が角度ペナルティ込みでゲートを僅かに
 * 超えて候補ゼロ → 未スナップ強制になり、77m の道路外ランが残った (実測: 本川小 A7)。
 */
const DEFAULT_MAX_SNAP_M = 30;
const DEFAULT_MAX_ANGLE_DEG = 50;
/** スナップ後に潰れた連続頂点をまとめる距離 (m)。 */
const MERGE_EPS_M = 2;
/**
 * スナップしない選択の定額コスト (m相当)。これ未満の射影距離ならスナップが勝つ。
 *
 * 候補ゲート (maxSnap=25) より大きい 28 にする = 「圏内に可視車道があるなら
 * 常に車道上へ描く」を不等式で成立させる。20 のときは 17〜23m 並走する
 * 分離歩道・歩行者専用橋 (どちらも印刷スタイルでは見えない) が未スナップで
 * 勝ってしまい、可視の trunk 橋/street の横を浮いて走る矢印になった
 * (実測: 本川小 — 全候補で 53〜171m の道路外ラン)。生形状の保護は
 * 形状歪みコスト・スパイクガード・角度コストが引き続き担う。
 */
const UNSNAPPED_COST_M = 28;
/**
 * 線の乗り換えコスト (m相当)。フラッピング抑止。実在の角では角ごとに1回払う。
 * 12 だと「短い中間街路を1頂点だけ経由する乗り換え」(乗り換え×2=24) が
 * 「歩道のまま残る」(20+切替4×2=28) に僅差で負け、車道沿い歩道が経路に残る
 * (実測: 阪本小 C1 — 中間頂点が中心線から 5.9m の歩道に残った)。8 なら
 * スナップ側 (~22) < 歩道残留 (28)。フラッピングは形状歪みコスト・スパイクガード・
 * 角度コストも抑止に効くため 8 で足りる (フラッピング回帰テストで担保)。
 */
const LINE_CHANGE_COST_M = 8;
/** スナップ⇄非スナップの切替コスト (m相当)。孤立した1頂点だけの snap/unsnap を抑止。
 * 上げすぎると頂点数の少ない経路で正当なスナップ列まで課税して裏目になる (実測)。 */
const UNSNAP_SWITCH_COST_M = 4;
/** 1頂点あたりの候補線の上限 (近い順)。 */
const MAX_CANDS_PER_VERTEX = 6;
/** スパイクガード: スナップによる折れ角の悪化がこの角度を超えた頂点は原座標へ戻す。 */
const SPIKE_TURN_DEG = 45;
/**
 * 角補完: 交点が「経路の既存形状」からこの距離以内なら実在の角とみなす (m)。
 * - 中間頂点あり: 交点↔中間頂点 (歩道の内角など) の距離。
 * - 隣接ブラケット: 交点↔弦の垂直距離 (= 角のショートカット深さ)。
 * ブラケットや弦の長さは頂点密度に依存するだけで角の実在性を測らない (教訓57 —
 * 実測: ブラケット間116m・交点↔ブラケット60mで棄却され、手前折れが2度発生)。
 * 歩道の内角・角のショートカットは真の交差点から数〜20m なのでこの条件を満たし、
 * 平行線のゴミ交点は遠くへ落ちて弾かれる。
 */
const CORNER_NEAR_M = 30;
/**
 * 角補完: 「実在の角」とみなす2線の最小角度差 (度)。タイル分割で同一街路が
 * ほぼ平行な別 feature になったペアの「交点」は幾何的なゴミ (実測: 5m の逆走 +
 * 179° 折れが挿入された) — 角度差が十分ある交差だけを補完する。
 */
const CORNER_MIN_ANGLE_DEG = 30;
/**
 * 未スナップ島の除去: 同一線 (または実質平行な別feature) に挟まれた未スナップ区間を
 * 除去する際の最大迂回量 (島の弧長 − フランク間の弦長, m)。横断歩道・歩道角の名残は
 * 迂回 10-31m (実測: 横浜 A1 — 弧50.6m/弦19.5m=迂回31.1m が最大)。中央分離帯を
 * 大きく回り込む実在の迂回 (コの字) は 60m 級なので、40m を境にする。
 */
const ISLAND_DETOUR_MAX_M = 40;
/**
 * 直角化: アンカー (始点/終点) から採用中心線までの最大距離 (m)。
 * 学校の門などの入口アンカーは街路から 30-40m 内側にあることがあり (実測: 阪本小の
 * SE 入口)、30m だと終点側の直角化が発動せず最終アプローチが斜めに街区を横切る。
 * 大型街区のビルは始点アンカーが道路から 55m 級 (実測: 横浜ランドマークタワー —
 * 45m では見送られ、広場を斜めに横断する 96m のリードが残った)。採用線が広場の
 * 反対側を走るケースでは接続が 65-80m 級になるため 80m。
 * 置換の妥当性は迂回量ガード (SQUARE_LEAD_DETOUR_SLACK_M) と折返しガードが担保する。
 */
const SQUARE_ANCHOR_MAX_M = 80;
/**
 * 直角化 (始点側): 置き換える区間の弧長が「垂線 + 足→合流点 + この余裕」を
 * 超える場合は中止 (m)。実経路が大きく迂回しているのに直線で置き換えると建物を
 * 貫く矢印を捏造するため。**固定の弧長上限にしない** — 歩道を長く並走してから
 * 車道に乗るだけの素直な経路 (迂回ゼロ) は弧長が長くても置き換えてよい
 * (実測: 札幌パークホテル — 65m の歩道並走で固定60mガードに阻まれ、8.6m の
 * オフセットを 65m かけて斜めに閉じるリードインが残った)。
 */
const SQUARE_LEAD_DETOUR_SLACK_M = 25;
/**
 * 直角化: 足→スナップ点→次 の折れがこれを超える場合は中止。実在の交差点の角は
 * 高々 ~95° — それ以上は「線の選び違いによる折返し楔」(実測: 阪本小 A4/A7/A12 で
 * [121°,116°] のくさびが残った)。矩形化を諦めて素の斜め結線に留める方がまし。
 */
const SQUARE_MAX_JUNCTION_TURN_DEG = 100;
/**
 * 直角化 (終点側): 垂線の足1点で置き換えてよい実経路の長さ (m)。
 *
 * 終点側に弧長ガードが無いのは、校庭・通用路の斜行 (実測: 阪本小 東門で 70m 級) を
 * 「道路を門の正面まで進んで垂直に入る」形へ置き換えたいため。ただし**無制限**だと、
 * 最後にスナップした頂点がずっと手前にある場合に、公道を歩く区間ごと消してしまう。
 *
 * 実測 (プラウドフラット横浜の避難所): 155m ぶんの実経路が、アンカーから 38m 横に
 * ある垂線の足1点に置き換わり、避難所の手前で西へ張り出して戻る線になった
 * (置き換え後の区間は道路中心線から最大 36m 外れる = 街区を横切る)。
 *
 * 敷地内のアプローチを直す用途には 120m あれば足り、公道区間の消去は防げる。
 */
const SQUARE_END_ARC_MAX_M = 120;

interface Seg {
  ax: number;
  ay: number;
  dx: number;
  dy: number;
  l2: number;
  angle: number; // [0, 180)
  line: number;
}

interface Cand {
  x: number;
  y: number;
  distM: number;
  /** null = スナップしない (原座標)。 */
  seg: Seg | null;
}

/**
 * 経路の中間頂点を、近傍で並走する道路中心線へ射影する。
 * 始点・終点は境界アンカー (物件縁・入口) なので動かさない。
 * 候補が無い頂点・一貫性コストで不利な頂点は原座標を保つ。
 */
export function snapRouteToRoads(
  route: Array<[number, number]>,
  roadLines: Array<Array<[number, number]>>,
  opts: SnapRouteOptions = {},
): Array<[number, number]> {
  const maxSnapM = opts.maxSnapM ?? DEFAULT_MAX_SNAP_M;
  const maxAngleDeg = opts.maxAngleDeg ?? DEFAULT_MAX_ANGLE_DEG;
  const avoidRings = opts.avoidRings ?? [];
  if (route.length < 3 || roadLines.length === 0) return route;

  const kx = Math.cos((route[0]![1] * Math.PI) / 180) * 111_320;
  const ky = 110_540;
  const toM = (p: [number, number]): [number, number] => [p[0] * kx, p[1] * ky];
  const toDeg = (m: [number, number]): [number, number] => [
    m[0] / kx,
    m[1] / ky,
  ];

  const segs: Seg[] = [];
  roadLines.forEach((line, li) => {
    for (let i = 1; i < line.length; i++) {
      const [ax, ay] = toM(line[i - 1]!);
      const [bx, by] = toM(line[i]!);
      const dx = bx - ax;
      const dy = by - ay;
      const l2 = dx * dx + dy * dy;
      if (l2 <= 0) continue;
      const angle = ((Math.atan2(dy, dx) * 180) / Math.PI + 180) % 180;
      segs.push({ ax, ay, dx, dy, l2, angle, line: li });
    }
  });
  if (segs.length === 0) return route;

  const ringsM = avoidRings
    .filter((r) => r.length >= 3)
    .map((r) => r.map(toM));

  const pts = route.map(toM);

  // 頂点ごとの候補 (線ごとの最近射影 + 「スナップしない」)。始終点は原座標に固定。
  const cands: Cand[][] = route.map((_, i) => {
    const [px, py] = pts[i]!;
    const unsnapped: Cand = { x: px, y: py, distM: 0, seg: null };
    if (i === 0 || i === route.length - 1) return [unsnapped];
    // 局所進行方向。潰れている場合は前後それぞれの差で代用し、それでもゼロなら候補なし。
    let vx = pts[i + 1]![0] - pts[i - 1]![0];
    let vy = pts[i + 1]![1] - pts[i - 1]![1];
    if (vx * vx + vy * vy <= 0) {
      vx = pts[i + 1]![0] - px;
      vy = pts[i + 1]![1] - py;
    }
    if (vx * vx + vy * vy <= 0) {
      vx = px - pts[i - 1]![0];
      vy = py - pts[i - 1]![1];
    }
    if (vx * vx + vy * vy <= 0) return [unsnapped];
    const dirAngle = ((Math.atan2(vy, vx) * 180) / Math.PI + 180) % 180;

    const perLine = new Map<number, Cand>();
    for (const s of segs) {
      let diff = Math.abs(s.angle - dirAngle);
      if (diff > 90) diff = 180 - diff;
      let t = ((px - s.ax) * s.dx + (py - s.ay) * s.dy) / s.l2;
      t = Math.max(0, Math.min(1, t));
      const x = s.ax + t * s.dx;
      const y = s.ay + t * s.dy;
      // 角度は**ハードゲートでなくコスト**にする。横断歩道の生ジグザグでは局所の
      // 進行方向が街路と直交し、ゲートだと候補ゼロ = 生座標のまま残って楔になる
      // (実測: 阪本小 A1 の [121°,116°])。角度ペナルティ込みの実効距離で評価すれば、
      // 孤立した直交スナップはコスト負けし、同一線に沿う文脈では DP の一貫性が
      // 正しい線へ吸わせる。
      const anglePenalty = Math.min(
        20,
        Math.max(0, diff - maxAngleDeg / 3) * 0.25,
      );
      const distM = Math.hypot(px - x, py - y) + anglePenalty;
      if (distM > maxSnapM) continue;
      if (ringsM.some((r) => inRingDeep([x, y], r))) continue;
      const cur = perLine.get(s.line);
      if (!cur || distM < cur.distM) {
        perLine.set(s.line, { x, y, distM, seg: s });
      }
    }
    const snaps = [...perLine.values()]
      .sort((a, b) => a.distM - b.distM)
      .slice(0, MAX_CANDS_PER_VERTEX);
    return [...snaps, unsnapped];
  });

  // Viterbi: 頂点コスト = 射影距離 (非スナップは定額)、遷移コスト = 形状歪み +
  // 線の乗り換え + snap/unsnap 切替。経路全体で総コスト最小の割当を選ぶ。
  const INF = Number.POSITIVE_INFINITY;
  // 頂点コスト: スナップ = 射影距離、スナップしない = 定額 (始終点は固定なので 0)。
  const costs: number[][] = cands.map((cs, i) =>
    cs.map((c) => {
      if (c.seg) return c.distM;
      return i === 0 || i === route.length - 1 ? 0 : UNSNAPPED_COST_M;
    }),
  );
  const total: number[][] = cands.map((cs) => cs.map(() => INF));
  const back: number[][] = cands.map((cs) => cs.map(() => -1));
  total[0] = costs[0]!.map((c) => c);
  for (let i = 1; i < route.length; i++) {
    const dOrig = Math.hypot(
      pts[i]![0] - pts[i - 1]![0],
      pts[i]![1] - pts[i - 1]![1],
    );
    cands[i]!.forEach((cur, ci) => {
      let bestPrev = INF;
      let bestIdx = -1;
      cands[i - 1]!.forEach((prev, pi) => {
        const base = total[i - 1]![pi]!;
        if (base === INF) return;
        const dSnap = Math.hypot(cur.x - prev.x, cur.y - prev.y);
        let trans = Math.abs(dSnap - dOrig);
        if (cur.seg && prev.seg && cur.seg.line !== prev.seg.line) {
          trans += LINE_CHANGE_COST_M;
        }
        if (!cur.seg !== !prev.seg) trans += UNSNAP_SWITCH_COST_M;
        const v = base + trans;
        if (v < bestPrev) {
          bestPrev = v;
          bestIdx = pi;
        }
      });
      total[i]![ci] = bestPrev + costs[i]![ci]!;
      back[i]![ci] = bestIdx;
    });
  }
  let ci = 0;
  {
    let best = INF;
    total[route.length - 1]!.forEach((v, k) => {
      if (v < best) {
        best = v;
        ci = k;
      }
    });
  }
  const picked: Cand[] = new Array(route.length);
  for (let i = route.length - 1; i >= 0; i--) {
    picked[i] = cands[i]![ci]!;
    ci = back[i]![ci]!;
    if (i > 0 && ci < 0) ci = 0;
  }

  const out: Array<[number, number]> = picked.map((c, i) =>
    c.seg ? toDeg([c.x, c.y]) : route[i]!,
  );
  const chosen: Array<Seg | null> = picked.map((c) => c.seg);

  // スパイクガード: スナップで折れ角が元より大きく悪化した頂点は原座標へ戻す。
  // 戻しで隣の角が変わるため 2 パスで安定させる。
  for (let pass = 0; pass < 2; pass++) {
    let reverted = false;
    for (let i = 1; i < out.length - 1; i++) {
      if (out[i] === route[i]) continue;
      const turnNow = turnDegAt(toM(out[i - 1]!), toM(out[i]!), toM(out[i + 1]!));
      const turnOrig = turnDegAt(pts[i - 1]!, pts[i]!, pts[i + 1]!);
      if (turnNow > turnOrig + SPIKE_TURN_DEG) {
        out[i] = route[i]!;
        chosen[i] = null;
        reverted = true;
      }
    }
    if (!reverted) break;
  }

  // 角補完: 別々の線に乗った2頂点の間に短い未スナップ区間 (歩道の角など) が挟まる
  // とき、その区間を**中心線同士の交点**に置換する。未スナップの角頂点は後段の間引きで
  // 消えやすく、消えると弦が交差点の角の街区を斜めに横切る (実測: 阪本小学校の候補)。
  for (let i = 0; i < out.length - 1; i++) {
    const si = chosen[i];
    if (!si) continue;
    let j = i + 1;
    while (j < out.length && !chosen[j] && j - i <= 4) j++;
    if (j >= out.length || !chosen[j]) continue;
    const sj = chosen[j]!;
    let angDiff = Math.abs(si.angle - sj.angle);
    if (angDiff > 90) angDiff = 180 - angDiff;
    const pi = toM(out[i]!);
    const pj = toM(out[j]!);
    const chord = Math.hypot(pj[0] - pi[0], pj[1] - pi[1]);
    if (si.line === sj.line || angDiff < CORNER_MIN_ANGLE_DEG) {
      // 同一線 (タイル分割・並走で実質同一方向の別 feature を含む) に挟まれた
      // 未スナップ島は、横断歩道・歩道角の名残 (スナップすると射影順が反転して
      // 180° 折れになり、スパイクガードが原座標へ戻した頂点)。挿入すべき角の
      // 交点が存在しないので島ごと除去する — 線上の2点を結ぶ弦は道なり
      // (実測: 横浜 A1 — 車道から 5-11m 浮いた島が3箇所残った)。
      if (j - i >= 2) {
        let arc = 0;
        for (let k = i + 1; k <= j; k++) {
          const a = toM(out[k - 1]!);
          const b = toM(out[k]!);
          arc += Math.hypot(b[0] - a[0], b[1] - a[1]);
        }
        if (arc - chord <= ISLAND_DETOUR_MAX_M) {
          out.splice(i + 1, j - i - 1);
          chosen.splice(i + 1, j - i - 1);
        }
      }
      continue;
    }
    const denom = si.dx * sj.dy - si.dy * sj.dx;
    if (Math.abs(denom) < 1e-9) continue; // ほぼ平行
    const t = ((sj.ax - si.ax) * sj.dy - (sj.ay - si.ay) * sj.dx) / denom;
    const X: [number, number] = [si.ax + t * si.dx, si.ay + t * si.dy];
    if (j - i >= 2) {
      // 中間頂点あり: 「交点が中間頂点 (歩道の内角など) の近く」を実在の角の条件に
      // する。ブラケット弦長は直線街路のスナップ頂点密度に依存するだけで角の実在性と
      // 無関係 (実測: 116m 弦で棄却され、角が歩道の内角=交差点の手前に残った)。
      let dmin = Infinity;
      for (let k = i + 1; k < j; k++) {
        const q = toM(out[k]!);
        dmin = Math.min(dmin, Math.hypot(X[0] - q[0], X[1] - q[1]));
      }
      if (dmin > CORNER_NEAR_M) continue;
    } else {
      // 隣接ブラケット: 「交点から弦への垂直距離 = 角のショートカット深さ」で判定する。
      // 交点↔ブラケット距離は第2の線上の頂点密度に依存し、角の実在性を測らない
      // (実測: 交差点の 15m 手前 × 60m 先のブラケットで棄却され、手前折れが再発)。
      const cx = pj[0] - pi[0];
      const cy = pj[1] - pi[1];
      const clen = Math.hypot(cx, cy);
      const sag =
        clen > 0
          ? Math.abs(cx * (X[1] - pi[1]) - cy * (X[0] - pi[0])) / clen
          : Math.hypot(X[0] - pi[0], X[1] - pi[1]);
      if (sag > CORNER_NEAR_M) continue;
    }
    // 角の頂点はブラケット間に射影されるはず (外なら逆走スパイクになるためスキップ)
    const proj =
      ((X[0] - pi[0]) * (pj[0] - pi[0]) + (X[1] - pi[1]) * (pj[1] - pi[1])) /
      (chord > 0 ? chord : 1);
    if (proj < 0 || proj > chord) continue;
    if (ringsM.some((r) => inRingDeep(X, r))) continue;
    out.splice(i + 1, j - i - 1, toDeg(X));
    chosen.splice(i + 1, j - i - 1, null);
    i += 1;
  }

  // 直角化: 始点・終点アンカーは境界上の固定点で中心線に乗らない。アンカー→最初の
  // スナップ点を素朴に結ぶと「道に対して浅い角度の斜め線」が長く走る (実測: 阪本小 —
  // 経路全体が車道に対して角度を持って見える)。アンカーから採用線への**垂線の足**を
  // 挿入し (間の未スナップ頂点は置換)、「垂直に道へ出て、道なりに進み、垂直に道から
  // 入る」スキーマティックな形にする。
  const squareAnchor = (fromStart: boolean) => {
    let j = -1;
    if (fromStart) {
      for (let i = 1; i < out.length - 1; i++) {
        if (chosen[i]) {
          j = i;
          break;
        }
      }
    } else {
      for (let i = out.length - 2; i >= 1; i--) {
        if (chosen[i]) {
          j = i;
          break;
        }
      }
    }
    if (j < 0) return;
    const anchorIdx = fromStart ? 0 : out.length - 1;
    const seg = chosen[j]!;
    const a = toM(out[anchorIdx]!);
    // 垂線の足: 第一候補は従来どおり「スナップ点セグメントの直線への垂線」
    // (真の直角接続)。それが上限を超える場合のみ「採用線の実ジオメトリ上の
    // 最近接点」へフォールバックする — 採用線がカーブしていると直線距離だけ
    // 上限を超えて直角化が丸ごと見送られ、広場を斜めに横断するリードが残る
    // (実測: 横浜ランドマークタワー)。常に最近接点にしないのは、タイル分割で
    // 線が途中で終わる場合に足が線端へクランプされ垂直性を失うため
    // (実測: 阪本小 — 始点の折れが 88°→27° に劣化した)。
    const tPerp =
      ((a[0] - seg.ax) * seg.dx + (a[1] - seg.ay) * seg.dy) / seg.l2;
    let foot: [number, number] = [
      seg.ax + tPerp * seg.dx,
      seg.ay + tPerp * seg.dy,
    ];
    let offset = Math.hypot(a[0] - foot[0], a[1] - foot[1]);
    // 垂線の足のセグメント外への外挿は 25m まで (タイル分割で線が途切れた継ぎ足し
    // は許す)。それ以上の外挿は「道路の無い延長線上」に足を置いてしまう
    // (実測: 横浜ランドマークタワー — L字路の角の 50m 先に足が飛んだ)。
    const segLen = Math.sqrt(seg.l2);
    const overshootM =
      tPerp < 0 ? -tPerp * segLen : tPerp > 1 ? (tPerp - 1) * segLen : 0;
    if (offset > SQUARE_ANCHOR_MAX_M || overshootM > 25) {
      for (const s of segs) {
        if (s.line !== seg.line) continue;
        let t = ((a[0] - s.ax) * s.dx + (a[1] - s.ay) * s.dy) / s.l2;
        t = Math.max(0, Math.min(1, t));
        const fx = s.ax + t * s.dx;
        const fy = s.ay + t * s.dy;
        const d = Math.hypot(a[0] - fx, a[1] - fy);
        if (d < offset) {
          offset = d;
          foot = [fx, fy];
        }
      }
    }
    if (offset < MERGE_EPS_M || offset > SQUARE_ANCHOR_MAX_M) return;
    // 置き換える区間 (アンカー〜スナップ点) の弧長ガードは**始点側のみ**。
    // 終点側は適用しない: Directions は校庭・通用路を斜めに歩く実経路を返すことが
    // あり (実測: 阪本小 東門 — 校地内 70m の斜行が「道なし対角弦」に見えた)、
    // 弧長ガードがその置換をブロックしていた。印刷矢印は「見える道路を門の正面まで
    // 進み、垂直に入る」形が正 (ユーザー方針)。ダイブの見た目の長さは
    // offset ≤ SQUARE_ANCHOR_MAX_M で別途有界。
    if (fromStart) {
      let arc = 0;
      for (let i = 1; i <= j; i++) {
        const p = toM(out[i - 1]!);
        const q = toM(out[i]!);
        arc += Math.hypot(q[0] - p[0], q[1] - p[1]);
      }
      const pjM = toM(out[j]!);
      const footToJ = Math.hypot(pjM[0] - foot[0], pjM[1] - foot[1]);
      if (arc > offset + footToJ + SQUARE_LEAD_DETOUR_SLACK_M) return;
    }
    if (!fromStart) {
      // 弧長ガード (終点側): 足1点で置き換える実経路が長すぎるなら直角化しない。
      // 置き換え対象は「最後のスナップ点 → アンカー」。敷地内のアプローチなら
      // 数十m で収まる。公道を歩く区間まで飲み込むと、道を外れた線になる。
      let arc = 0;
      for (let i = j; i < anchorIdx; i++) {
        const q0 = toM(out[i]!);
        const q1 = toM(out[i + 1]!);
        arc += Math.hypot(q1[0] - q0[0], q1[1] - q0[1]);
      }
      if (arc > SQUARE_END_ARC_MAX_M) return;
    }
    // 折返しガード: 足→スナップ点→次 が U ターンに近いなら中止
    const pj = toM(out[j]!);
    const nextIdx = fromStart ? j + 1 : j - 1;
    if (nextIdx <= 0 || nextIdx >= out.length) return;
    const pn = toM(out[nextIdx]!);
    if (
      Math.hypot(pj[0] - foot[0], pj[1] - foot[1]) >= MERGE_EPS_M &&
      turnDegAt(foot, pj, pn) > SQUARE_MAX_JUNCTION_TURN_DEG
    ) {
      return;
    }
    if (ringsM.some((r) => inRingDeep(foot, r))) return;
    // 直角化リード (アンカー→足) が物件フットプリント等の内部を横切るなら中止し、
    // 生の道沿いリードを残す。素朴な直線化は凹形の建物の角を掠める (実測: 横浜
    // ランドマークタワー — 生経路は歩道で建物を回り込むのに、直角化が 92m の弦で
    // 角を 0.93m 横断した)。矢印は建物の縁の道を沿うのが正 (ユーザー方針)。
    if (fromStart && ringsM.some((r) => segmentEntersRing(a, foot, r))) return;
    const footDeg = toDeg(foot);
    if (fromStart) {
      out.splice(1, j - 1, footDeg);
      chosen.splice(1, j - 1, seg);
    } else {
      out.splice(j + 1, out.length - 1 - (j + 1), footDeg);
      chosen.splice(j + 1, chosen.length - 1 - (j + 1), seg);
    }
  };
  squareAnchor(true);
  squareAnchor(false);

  // 直角化が成立しなかったリード (道なし回廊など) は、アンカー→最初のスナップ点
  // の間の未スナップ頂点を**一直線化**する。生の折れ (実測: 横浜ランドマークタワー
  // — 12m+84m のくの字) を残すより、1本の直線接続の方が矢印の文法に近い。
  // 実経路が大きく迂回している場合 (弧長 ≫ 弦長) は形を尊重して触らない。
  const straightenLead = (fromStart: boolean) => {
    let j = -1;
    if (fromStart) {
      for (let i = 1; i < out.length - 1; i++) {
        if (chosen[i]) {
          j = i;
          break;
        }
      }
    } else {
      for (let i = out.length - 2; i >= 1; i--) {
        if (chosen[i]) {
          j = i;
          break;
        }
      }
    }
    const anchorIdx = fromStart ? 0 : out.length - 1;
    const lo = fromStart ? 0 : j;
    const hi = fromStart ? j : out.length - 1;
    if (j < 0 || hi - lo < 2) return;
    let arc = 0;
    for (let i = lo + 1; i <= hi; i++) {
      const p0 = toM(out[i - 1]!);
      const p1 = toM(out[i]!);
      arc += Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
    }
    const aM = toM(out[anchorIdx]!);
    const jM = toM(out[j]!);
    const chord = Math.hypot(jM[0] - aM[0], jM[1] - aM[1]);
    if (arc > chord * 1.15 + 10) return; // 実迂回 → 触らない
    // 直線化した弦が物件フットプリントを横切るなら中止 (道沿いの生リードを残す)。
    if (fromStart && ringsM.some((r) => segmentEntersRing(aM, jM, r))) return;
    out.splice(lo + 1, hi - lo - 1);
    chosen.splice(lo + 1, hi - lo - 1);
  };
  straightenLead(true);
  straightenLead(false);

  // スナップで同一点に潰れた連続頂点をまとめる (始終点は保持)
  const merged: Array<[number, number]> = [out[0]!];
  for (let i = 1; i < out.length; i++) {
    const prev = toM(merged[merged.length - 1]!);
    const cur = toM(out[i]!);
    const isLast = i === out.length - 1;
    if (Math.hypot(cur[0] - prev[0], cur[1] - prev[1]) < MERGE_EPS_M) {
      // 終点と衝突した場合は直前の中間点の方を落とす
      if (isLast && merged.length > 1) merged.pop();
      else if (!isLast) continue;
    }
    merged.push(out[i]!);
  }
  return merged;
}

/** b での折れ角 (度, 0=直進)。 */
function turnDegAt(
  a: [number, number],
  b: [number, number],
  c: [number, number],
): number {
  const v1x = b[0] - a[0];
  const v1y = b[1] - a[1];
  const v2x = c[0] - b[0];
  const v2y = c[1] - b[1];
  if ((v1x === 0 && v1y === 0) || (v2x === 0 && v2y === 0)) return 0;
  return (
    (Math.atan2(
      Math.abs(v1x * v2y - v1y * v2x),
      v1x * v2x + v1y * v2y,
    ) *
      180) /
    Math.PI
  );
}

/**
 * 回避リングの「深部」とみなす境界からの距離 (m)。公式ポリゴンは周囲の道路に
 * 被さるほど大きく描かれることがあり (実測: 阪本小のリングが隣接街路を内包)、
 * 内側を一律拒否すると外周街路へスナップできず経路が境界線を這う。浅い被り
 * (境界から ≤ この値) は許し、深い内部 (地区の内部公道等) だけを拒否する。
 */
const MAX_SNAP_RING_DEPTH_M = 8;

/** 点がリングの深部 (内側 かつ 境界から MAX_SNAP_RING_DEPTH_M 超) にあるか。 */
function inRingDeep(
  p: [number, number],
  ring: Array<[number, number]>,
): boolean {
  if (!pointInRingM(p, ring)) return false;
  let best = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [ax, ay] = ring[j]!;
    const [bx, by] = ring[i]!;
    const dx = bx - ax;
    const dy = by - ay;
    const l2 = dx * dx + dy * dy;
    let t = l2 > 0 ? ((p[0] - ax) * dx + (p[1] - ay) * dy) / l2 : 0;
    t = Math.max(0, Math.min(1, t));
    best = Math.min(
      best,
      Math.hypot(p[0] - (ax + t * dx), p[1] - (ay + t * dy)),
    );
    if (best <= MAX_SNAP_RING_DEPTH_M) return false;
  }
  return best > MAX_SNAP_RING_DEPTH_M;
}

/**
 * 線分 a→b がリング (メートル空間) の内部を通るか。端点はアンカーで境界上に載る
 * ことがあるため内部サンプル (2m 刻み) のみで判定する。物件フットプリントは高さの
 * ある建物 = **深さゼロの掠めも不可**なので inRingDeep でなくこちらで見る。
 */
function segmentEntersRing(
  a: [number, number],
  b: [number, number],
  ring: Array<[number, number]>,
): boolean {
  const n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / 2));
  for (let k = 1; k < n; k++) {
    const t = k / n;
    if (pointInRingM([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t], ring)) {
      return true;
    }
  }
  return false;
}

/** 点 p がリング (メートル空間) の内側か (even-odd ray cast)。 */
function pointInRingM(
  p: [number, number],
  ring: Array<[number, number]>,
): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (
      yi > p[1] !== yj > p[1] &&
      p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
}
